import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Minus, Plus, Sun, Moon, Clock, LogIn, Zap, Repeat, ChevronDown, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Carousel, CarouselContent, CarouselItem } from "../../components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { formatINR, todayISO } from "../../lib/utils.fmt";
import { getTenantSlug } from "../../lib/tenant";
import AddressDialog from "../../components/AddressDialog";
import CustomerHeader from "../../components/CustomerHeader";
import ProductImage from "../../components/ProductImage";
import { earliestDeliveryDate, isWindowOrderable } from "../../lib/cutoff";
import { toast } from "sonner";
import { tomorrowIST } from "../../lib/istDate";

const DAYS = [
  { v: 0, key: "day_mon" }, { v: 1, key: "day_tue" }, { v: 2, key: "day_wed" },
  { v: 3, key: "day_thu" }, { v: 4, key: "day_fri" }, { v: 5, key: "day_sat" }, { v: 6, key: "day_sun" },
];

// IST calendar date (lib/istDate) — matches the backend's cutoff calendar.
function tomorrowISO() {
  return tomorrowIST();
}

// Hero height ramp — bumped at wider breakpoints since CustomerLayout.jsx
// scales the whole app shell up to xl:max-w-6xl on desktop/iPad; a fixed
// mobile-sized hero looked disproportionately short there.
//
// Written as literal "h-64 sm:h-72 md:h-80 lg:h-96" directly in each
// className below (not via a shared JS variable interpolated into a
// template literal) — Tailwind's JIT scanner extracts class names from the
// raw text of className expressions, and routing them through a shared
// constant is a known footgun for that. Also: the long-running dev server
// needed a full restart (not just HMR) to reliably pick up md:h-80/h-96
// after they were first introduced — if a height class you just added isn't
// taking effect, restart the dev server before assuming the code is wrong.

// Product hero image(s). Renders the original single <img> when there's
// only one image (the overwhelming majority of existing products —
// identical markup/data-testid to before, zero behavior change). Only
// swaps in the embla carousel + autoplay + dot indicators when a product
// actually has multiple images.
function ProductHeroImage({ images, alt, primary }) {
  const [api, setApi] = useState(null);
  const [idx, setIdx] = useState(0);
  // Stable plugin instance AND a stable array wrapping it — ProductDetail
  // re-renders often (quantity/slot/address changes), and a fresh plugins
  // array each time would risk the carousel silently reinitializing,
  // resetting position and interrupting autoplay's timer.
  const autoplayRef = useRef(
    Autoplay({ delay: 3500, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const [plugins] = useState(() => [autoplayRef.current]);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setIdx(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);

  if (!images || images.length <= 1) {
    // ProductImage handles both a missing src AND a broken URL with a
    // themed initials badge — a bare <img src={images?.[0] || ""}> was
    // rendering a broken-image icon + raw alt text spilling into the hero
    // box for any product with no photo (also an invalid empty `src`,
    // which browsers resolve as a request for the current document).
    return <ProductImage src={images?.[0]} name={alt} primary={primary} className="w-full h-full"
                          badgeClassName="w-20 h-20 text-2xl" />;
  }

  return (
    <>
      <Carousel setApi={setApi} opts={{ loop: true }} plugins={plugins} className="w-full h-full">
        <CarouselContent className="-ml-0 h-full">
          {images.map((src, i) => (
            <CarouselItem key={i} className="pl-0 h-64 sm:h-72 md:h-80 lg:h-96">
              <img src={src} alt={`${alt} ${i + 1}`} className="w-full h-full object-cover" data-testid={`product-image-${i}`} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10" data-testid="product-image-dots">
        {images.map((_, i) => (
          <button key={i} onClick={() => api?.scrollTo(i)}
                  className={`h-2 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-2 bg-white/60"}`}
                  aria-label={`Image ${i + 1}`} data-testid={`product-image-dot-${i}`} />
        ))}
      </div>
    </>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, tenant } = useAuth();
  const { t } = useT();
  const [p, setP] = useState(null);
  const [brands, setBrands] = useState([]);
  const [mode, setMode] = useState("once");      // "once" | "subscribe"
  const [qty, setQty] = useState(1);
  const [freq, setFreq] = useState("daily");      // subscribe-mode only
  const [days, setDays] = useState([]);            // weekly/custom — default empty
  const [slot, setSlot] = useState("morning");
  const [startDate, setStartDate] = useState(tomorrowISO());
  const [deliveryDate, setDeliveryDate] = useState(tomorrowISO());
  // Tracks whether the customer has manually touched the date picker — the
  // sync-to-earliest effect below only auto-updates the default while this
  // is false, mirroring Checkout.jsx's identical fix.
  const [dateTouched, setDateTouched] = useState(false);
  const [instructions, setInstructions] = useState("Leave at door");
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addrDialogOpen, setAddrDialogOpen] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  const [loadError, setLoadError] = useState(null);

  const reloadAddresses = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/api/customer/addresses");
      setAddresses(data.addresses);
      // Prefer the account-wide default (same as Home/Checkout) over raw
      // array position, so the pre-selected address is consistent everywhere.
      if (data.addresses.length && !addressId) {
        const def = data.addresses.find((a) => a.is_default);
        setAddressId((def || data.addresses[0]).id);
      }
    } catch { /* non-critical secondary data — product itself still loads */ }
  };

  const loadProduct = () => {
    setLoadError(null);
    const slug = getTenantSlug();
    // Load brands (for brand badge) — public endpoint works for guests too
    api.get(`/api/public/tenant/${slug}/home`).then(({ data }) => setBrands(data.brands || [])).catch(() => {});
    if (user) {
      api.get(`/api/customer/products/${id}`).then(({ data }) => setP(data))
        .catch((e) => setLoadError(friendlyMessage(e)));
      api.get("/api/customer/addresses").then(({ data }) => {
        setAddresses(data.addresses);
        if (data.addresses.length) {
          const def = data.addresses.find((a) => a.is_default);
          setAddressId((def || data.addresses[0]).id);
        }
      }).catch(() => {});
    } else {
      api.get(`/api/public/tenant/${slug}/products/${id}`).then(({ data }) => setP(data))
        .catch((e) => setLoadError(friendlyMessage(e)));
    }
  };
  useEffect(() => { loadProduct(); /* eslint-disable-next-line */ }, [id, user]);

  // Reset variant selection whenever the product changes (default = variants[0]).
  useEffect(() => {
    setSelectedVariantId(p?.variants?.[0]?.id ?? null);
  }, [p?.id]);

  // Keep the "buy now" delivery date synced to the cutoff-computed earliest
  // date (tenant.delivery_windows may allow same-day, not just tomorrow) as
  // long as the customer hasn't picked a date themselves — previously this
  // always defaulted to tomorrowISO() with no correction at all, so a
  // same-day-eligible tenant's customers had to notice and manually reopen
  // the date picker. Same fix as Checkout.jsx.
  useEffect(() => {
    const earliest = earliestDeliveryDate(tenant?.delivery_windows || []) || todayISO();
    if (!dateTouched) {
      if (deliveryDate !== earliest) setDeliveryDate(earliest);
    } else if (deliveryDate < earliest) {
      setDeliveryDate(earliest);
    }
  }, [tenant?.delivery_windows, deliveryDate, dateTouched]);

  const toggleDay = (d) => {
    setDays((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort());
  };

  const submit = async () => {
    if (!user) {
      nav(`/login?return=${encodeURIComponent(`/app/products/${id}`)}`);
      return;
    }
    if (!addressId) { toast.error(t("please_add_address_first")); return; }
    setSubmitting(true);
    try {
      if (mode === "once") {
        await api.post("/api/customer/orders/one-time", {
          product_id: id, variant_id: selectedVariant?.id ?? null, quantity: qty, slot,
          delivery_date: deliveryDate, address_id: addressId, instructions,
        });
        toast.success(t("order_placed"));
        nav("/app/orders");
      } else {
        if ((freq === "weekly" || freq === "custom") && days.length === 0) {
          toast.error(t("pick_at_least_one_day")); setSubmitting(false); return;
        }
        await api.post("/api/customer/subscriptions", {
          product_id: id, variant_id: selectedVariant?.id ?? null, quantity: qty,
          frequency: freq,
          custom_days: (freq === "weekly" || freq === "custom") ? days : [],
          slot, start_date: startDate, address_id: addressId, instructions,
        });
        toast.success(t("subscription_created"));
        nav("/app/subscriptions");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  if (loadError && !p) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load this product" description={loadError}
        action={<button onClick={loadProduct} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }
  if (!p) return <LoadingState fullHeight />;

  const primary = tenant?.branding?.primary_color || "#3A86FF";
  // Variant picker — only shown/active when the product actually has more
  // than one variant. Subscriptions and one-time orders are both
  // variant-aware (routes_customer.py resolves variant_id for either), so
  // the same selection applies regardless of mode.
  const variants = p.variants && p.variants.length > 1 ? p.variants : null;
  const selectedVariant = variants
    ? (variants.find((v) => v.id === selectedVariantId) || variants[0])
    : null;
  const displayPrice = selectedVariant ? selectedVariant.price : p.price;
  const displayMrp = selectedVariant ? selectedVariant.mrp : p.mrp;
  const displayPackSize = selectedVariant ? (selectedVariant.pack_size || p.pack_size) : p.pack_size;
  // effective_price = per-unit price after customer-specific discount. The
  // backend resolves the discount rule once per product and applies it to
  // every variant's own price (routes_customer.py), so this is correct for
  // whichever variant is currently selected, not just the default one.
  const displayEffectivePrice = selectedVariant ? selectedVariant.effective_price : p.effective_price;
  const unitPrice = displayEffectivePrice != null ? displayEffectivePrice : displayPrice;
  const hasPersonalDiscount = displayEffectivePrice != null && displayEffectivePrice < displayPrice;
  const perDeliveryAmount = unitPrice * qty;
  const monthlyEst = perDeliveryAmount * (
    freq === "daily" ? 30 :
    freq === "alternate" ? 15 :
    freq === "weekly" || freq === "custom" ? days.length * 4.3 : 1
  );

  return (
    <div className="pb-52 page-enter" data-testid="product-detail-page">
      <CustomerHeader primary={primary}/>
      <div className="relative h-64 sm:h-72 md:h-80 lg:h-96 bg-neutral-100 overflow-hidden">
        <ProductHeroImage images={p.images && p.images.length ? p.images : [p.image_url]} alt={p.name} primary={primary} />
        <Link to="/app/products" className="absolute top-3 left-3 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center z-20" data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Link>
      </div>

      <div className="px-5 -mt-8 relative space-y-5">
        <div className="bg-white rounded-3xl border border-neutral-200 p-6 shadow-sm">
          <div className="text-xs uppercase tracking-wider font-bold" style={{ color: primary }}>{p.category} · {displayPackSize}</div>
          <h1 className="font-heading text-2xl font-extrabold mt-1 tracking-tight">{p.name}</h1>
          {p.brand_id && (() => {
            const b = brands.find((x) => x.id === p.brand_id);
            if (!b) return null;
            return (
              <Link to={`/app/products?brand=${b.slug}`}
                    className="inline-flex items-center gap-2 mt-2 px-2.5 py-1 rounded-full bg-brand-cream border border-neutral-200 card-hover"
                    data-testid="product-brand-badge">
                {b.logo_url ? (
                  <img src={b.logo_url} alt={b.name} className="w-5 h-5 rounded object-contain"/>
                ) : (
                  <div className="w-5 h-5 rounded text-white text-[10px] font-extrabold flex items-center justify-center" style={{ background: primary }}>
                    {b.name[0]}
                  </div>
                )}
                <span className="text-xs font-bold">{t("by")} {b.name}</span>
              </Link>
            );
          })()}
          <div className="flex items-baseline gap-2 mt-2 flex-wrap">
            <span className="font-heading text-3xl font-bold">{formatINR(displayPrice)}</span>
            {displayMrp > displayPrice && (
              <span className="text-neutral-400 line-through">{formatINR(displayMrp)}</span>
            )}
            <span className="text-xs text-brand-leaf font-bold ml-auto">per {displayPackSize}</span>
          </div>
          {hasPersonalDiscount && (
            <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-leaf/10 text-brand-leaf text-xs font-extrabold uppercase tracking-wider"
                 data-testid="personal-discount-ribbon">
              Personal Discount available
            </div>
          )}
          <p className="text-sm text-neutral-700 mt-3 leading-relaxed">{p.description}</p>
        </div>

        {/* Variant picker — only rendered when the product has more than one variant */}
        {variants && (
          <div className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="variant-picker">
            <div className="font-semibold mb-3">Choose an option</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {variants.map((v) => {
                const active = selectedVariant?.id === v.id;
                const oos = v.is_in_stock === false;
                return (
                  <button key={v.id}
                          onClick={() => !oos && setSelectedVariantId(v.id)}
                          disabled={oos}
                          className={`h-auto py-2.5 px-2 rounded-2xl font-bold text-sm border-2 flex flex-col items-center justify-center gap-0.5 transition-colors ${active ? "text-white border-transparent" : "border-neutral-200 bg-white text-neutral-700"} ${oos ? "opacity-40 cursor-not-allowed" : ""}`}
                          style={active ? { background: primary } : {}}
                          data-testid={`variant-${v.id}`}>
                    <span>{v.label || v.pack_size}</span>
                    <span className={`text-xs font-mono ${active ? "text-white/85" : "text-neutral-500"}`}>{formatINR(v.price)}</span>
                    {oos && <span className="text-[9px] uppercase tracking-wider font-extrabold text-brand-clay">Out of stock</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Order mode tabs */}
        <div className="bg-white rounded-3xl border border-neutral-200 p-2 grid grid-cols-2 gap-2" data-testid="order-mode-tabs">
          <button onClick={() => setMode("once")}
                  className={`h-12 rounded-2xl font-bold text-sm inline-flex items-center justify-center gap-2 transition-colors ${mode === "once" ? "text-white" : "text-neutral-600"}`}
                  style={mode === "once" ? { background: primary } : {}}
                  data-testid="mode-once">
            <Zap className="w-4 h-4"/> {t("order_once")}
          </button>
          <button onClick={() => setMode("subscribe")}
                  className={`h-12 rounded-2xl font-bold text-sm inline-flex items-center justify-center gap-2 transition-colors ${mode === "subscribe" ? "text-white" : "text-neutral-600"}`}
                  style={mode === "subscribe" ? { background: primary } : {}}
                  data-testid="mode-subscribe">
            <Repeat className="w-4 h-4"/> {t("subscribe_save")}
          </button>
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200 p-6">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-4">
            {mode === "once" ? t("one_time_order") : t("subscription_details")}
          </div>

          <div className="flex items-center justify-between mb-5">
            <span className="font-semibold">{t("quantity")}</span>
            <div className="flex items-center gap-3">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 rounded-full bg-brand-cream border border-neutral-200 flex items-center justify-center" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="font-heading text-xl font-bold w-8 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-10 rounded-full text-white flex items-center justify-center"
                      style={{ background: primary }} data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Subscribe-only: Frequency + Days */}
          {mode === "subscribe" && (
            <>
              <div className="mb-5">
                <div className="font-semibold mb-2">{t("frequency")}</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { v: "daily", l: t("freq_daily") },
                    { v: "alternate", l: t("freq_alternate") },
                    { v: "weekly", l: t("freq_weekly") },
                    { v: "custom", l: t("freq_custom") },
                  ].map((o) => (
                    <button key={o.v} onClick={() => setFreq(o.v)}
                            className={`h-12 rounded-2xl font-bold text-sm border-2 transition-colors ${freq === o.v ? "text-white border-transparent" : "border-neutral-200 bg-white text-neutral-700"}`}
                            style={freq === o.v ? { background: primary } : {}}
                            data-testid={`freq-${o.v}`}>{o.l}</button>
                  ))}
                </div>
              </div>

              {(freq === "weekly" || freq === "custom") && (
                <div className="mb-5">
                  <div className="font-semibold mb-2">{t("days_of_week")}</div>
                  <div className="grid grid-cols-7 gap-1.5" data-testid="day-chips">
                    {DAYS.map((d) => {
                      const active = days.includes(d.v);
                      return (
                        <button key={d.v} onClick={() => toggleDay(d.v)}
                                className={`h-12 rounded-2xl font-bold text-xs transition-colors ${active ? "text-white border-transparent" : "bg-white border-2 border-neutral-200 text-neutral-600"}`}
                                style={active ? { background: primary } : {}}
                                data-testid={`day-${d.v}`}>{t(d.key)}</button>
                      );
                    })}
                  </div>
                  <div className="text-xs text-neutral-500 mt-2">
                    {days.length === 1 ? t("days_per_week_one") : t("days_per_week_other", { n: days.length })}
                  </div>
                </div>
              )}

              <div className="mb-5">
                <div className="font-semibold mb-2">{t("start_date")}</div>
                <div className="relative">
                  <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" data-testid="start-date-icon" />
                  <input type="date" value={startDate} min={todayISO()} onChange={(e) => setStartDate(e.target.value)}
                         className="w-full h-12 rounded-2xl border border-neutral-200 pl-11 pr-4 text-base appearance-none bg-white"
                         data-testid="start-date" aria-label="Start date" />
                </div>
              </div>
            </>
          )}

          {/* One-time only: Delivery date */}
          {mode === "once" && (
            <div className="mb-5">
              <div className="font-semibold mb-2">{t("delivery_date")}</div>
              <div className="relative">
                <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" data-testid="calendar-icon" />
                <input type="date" value={deliveryDate}
                       min={earliestDeliveryDate(tenant?.delivery_windows || []) || todayISO()}
                       onChange={(e) => { setDateTouched(true); setDeliveryDate(e.target.value); }}
                       className="w-full h-12 rounded-2xl border border-neutral-200 pl-11 pr-4 text-base appearance-none bg-white"
                       data-testid="delivery_date" aria-label="Delivery date" />
              </div>
              <div className="text-xs text-neutral-500 mt-2">{t("next_available_slot")}</div>
            </div>
          )}

          {/* Slot — sourced from tenant.delivery_windows so only enabled
              slots are offered (falls back to morning+evening for legacy
              tenants with no windows configured). Adjusts to 3-col grid
              when the tenant has 3 windows so the cards stay readable on
              mobile. */}
          <div className="mb-5">
            <div className="font-semibold mb-2">{t("delivery_slot")}</div>
            {(() => {
              const wins = (tenant?.delivery_windows || []).filter((w) => w.is_active !== false);
              const opts = wins.length
                ? wins.map((w) => ({ v: (w.label || "").toLowerCase(), l: w.label, win: w }))
                : [
                    { v: "morning", l: t("slot_morning"), win: null },
                    { v: "evening", l: t("slot_evening"), win: null },
                  ];
              if (mode === "subscribe" && opts.length >= 2) {
                opts.push({ v: "both", l: t("slot_both"), win: null });
              }
              return (
                <div className={`grid gap-2 ${opts.length >= 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                  {opts.map(({ v, l, win }) => {
                    const Icon = v === "evening" ? Moon : v === "both" ? Clock : Sun;
                    const active = slot === v;
                    // For one-time orders, lock specific slots whose cutoff
                    // for the picked delivery date has passed. Subscribe mode
                    // is forward-looking so we don't block.
                    const disabled = mode === "once" && !!win && !isWindowOrderable(win, deliveryDate);
                    return (
                      <button key={v} onClick={() => !disabled && setSlot(v)}
                              disabled={disabled}
                              className={`h-auto py-2 rounded-2xl font-bold text-sm border-2 flex flex-col items-center justify-center gap-0.5 transition-colors ${active ? "text-white border-transparent" : "border-neutral-200 bg-white text-neutral-700"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                              style={active ? { background: primary } : {}}
                              data-testid={`slot-${v}`}>
                        <span className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4" /> {l}</span>
                        {win && (
                          <span className={`text-[10px] font-mono font-bold tabular-nums ${active ? "text-white/85" : "text-neutral-500"}`}>
                            {win.slot_start} – {win.slot_end}
                          </span>
                        )}
                        {disabled && (
                          <span className="text-[9px] uppercase tracking-wider font-extrabold text-brand-clay" data-testid={`slot-${v}-locked`}>
                            cutoff passed
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {(() => {
              const win = tenant?.delivery_windows?.find((w) => w.label?.toLowerCase() === slot);
              if (!win) return null;
              return (
                <div className="text-xs text-neutral-500 mt-2 inline-flex items-center gap-1.5" data-testid="slot-window">
                  <Clock className="w-3 h-3"/> Delivery between <b className="font-mono">{win.slot_start}</b> and <b className="font-mono">{win.slot_end}</b>{win.cutoff_time ? <> · order before <b className="font-mono">{win.cutoff_time}</b></> : null}
                </div>
              );
            })()}
          </div>

          <div className="mb-5">
            <div className="font-semibold mb-2">{t("delivery_address")}</div>
            {addresses.length === 0 ? (
              <button onClick={() => setAddrDialogOpen(true)}
                      className="w-full h-12 rounded-2xl border-2 border-dashed border-brand-sky text-brand-sky font-bold text-sm inline-flex items-center justify-center gap-2"
                      data-testid="inline-add-address-btn">
                + Add delivery address
              </button>
            ) : (
              <div className="flex gap-2 items-stretch w-full">
                <div className="flex-1 min-w-0 relative">
                  <select value={addressId} onChange={(e) => setAddressId(e.target.value)}
                          className="w-full h-12 rounded-2xl border border-neutral-200 pl-4 pr-9 text-sm bg-white appearance-none truncate" data-testid="address-select">
                    {addresses.map((a) => (
                      <option key={a.id} value={a.id}>{a.label} — {a.line1}{a.city ? `, ${a.city}` : ""}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none"/>
                </div>
                <button onClick={() => setAddrDialogOpen(true)}
                        className="shrink-0 h-12 px-3 rounded-2xl bg-brand-cream border border-neutral-200 text-brand-sky font-bold text-sm inline-flex items-center gap-1"
                        data-testid="add-another-address-btn">
                  <Plus className="w-3.5 h-3.5"/> New
                </button>
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className="font-semibold mb-2">{t("delivery_instructions")}</div>
            <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)}
                      className="rounded-2xl text-base" rows={2} data-testid="instructions-input" />
          </div>

          {/* Detailed price breakdown — visible when the customer taps
              into the product / picks a quantity. Makes MRP, selling
              price, and any personal discount transparent before they
              commit to the order. */}
          <div className="rounded-2xl bg-brand-cream p-4" data-testid="price-breakdown">
            {(displayMrp && displayMrp > displayPrice) && (
              <div className="flex justify-between text-sm py-1">
                <span className="text-neutral-500">MRP ({qty} × {formatINR(displayMrp)})</span>
                <span className="text-neutral-500 line-through">{formatINR(displayMrp * qty)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm py-1">
              <span className="text-neutral-700">Selling price ({qty} × {formatINR(displayPrice)})</span>
              <span className="font-semibold">{formatINR(displayPrice * qty)}</span>
            </div>
            {hasPersonalDiscount && (
              <div className="flex justify-between text-sm py-1" data-testid="personal-discount-line">
                <span className="text-brand-leaf font-semibold">Personal Discount</span>
                <span className="text-brand-leaf font-bold">− {formatINR((displayPrice - unitPrice) * qty)}</span>
              </div>
            )}
            <div className="border-t border-neutral-300 my-2"></div>
            <div className="flex justify-between items-baseline">
              <span className="font-heading font-bold text-neutral-800">
                {mode === "once" ? t("order_total") : "Per delivery"}
              </span>
              <span className="font-heading font-extrabold text-lg">
                {formatINR(perDeliveryAmount)}
              </span>
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              {mode === "once"
                ? `${qty} unit${qty > 1 ? "s" : ""} · ${slot}`
                : `${freq}${(freq === "weekly" || freq === "custom") ? ` (${days.length} d/wk)` : ""} × ${qty}`}
              {mode !== "once" && (
                <> · Est. {formatINR(monthlyEst)} / month</>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed floating CTA bar is a mobile pattern (thumb-reachable,
          screen space is scarce). On desktop the page is short enough
          relative to the viewport that a floating bar overlapped the
          form fields below it (quantity/date/slot) at the initial scroll
          position — a `fixed` element covers the same viewport pixels
          regardless of how tall the actual page content is. From md: up
          it reverts to a normal in-flow block at the end of the form,
          same as any other desktop web form's submit button. */}
      <div className="fixed bottom-20 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-neutral-200 p-4 z-40 pb-safe md:static md:translate-x-0 md:max-w-none md:mt-6 md:rounded-3xl md:border">
        <Button onClick={submit} disabled={submitting}
                className="w-full h-14 rounded-2xl font-bold text-base text-white"
                style={{ background: primary }}
                data-testid="submit-order-btn">
          {!user ? (
            <><LogIn className="w-4 h-4 mr-2"/> {t("sign_in")} — {formatINR(perDeliveryAmount)}</>
          ) : submitting ? t("placing") :
              mode === "once" ? `${t("place_order")} — ${formatINR(perDeliveryAmount)}` :
                                `${t("subscribe")} — ${formatINR(perDeliveryAmount)} / ${t("per_delivery")}`}
        </Button>
      </div>

      <AddressDialog open={addrDialogOpen} onOpenChange={setAddrDialogOpen} onSaved={reloadAddresses} />
    </div>
  );
}
